import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Eye, EyeOff } from "lucide-react";

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    let result;
    if (mode === "login") {
      result = await login(email, password);
    } else {
      if (!name.trim()) { setError("Name is required"); setLoading(false); return; }
      if (password.length < 6) { setError("Password must be at least 6 characters"); setLoading(false); return; }
      result = await register(name, email, password);
    }
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-earth-50 flex items-center justify-center px-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <img src="/sovha-logo.png" alt="Sovha" className="h-14 mx-auto mb-3" />
          <p className="text-earth-500 text-sm">Your big sister, always in your corner</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-[2rem] p-8 sm:p-10 shadow-[0_8px_30px_rgba(45,49,46,0.06)] border border-earth-200/50">
          {/* Tabs */}
          <div className="flex gap-1 bg-earth-100 rounded-full p-1 mb-8">
            <button onClick={() => { setMode("login"); setError(""); }} data-testid="auth-login-tab"
              className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${mode === "login" ? "bg-white text-earth-800 shadow-sm" : "text-earth-500"}`}>
              Sign in
            </button>
            <button onClick={() => { setMode("register"); setError(""); }} data-testid="auth-register-tab"
              className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${mode === "register" ? "bg-white text-earth-800 shadow-sm" : "text-earth-500"}`}>
              Create account
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-earth-600 text-xs font-semibold uppercase tracking-wider mb-1 block">Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="What should we call you?"
                  data-testid="auth-name-input" autoComplete="name"
                  className="bg-earth-100 border-0 rounded-2xl px-5 py-3.5 text-earth-800 w-full focus:ring-2 focus:ring-terracotta/30 focus:outline-none placeholder:text-earth-400 text-sm" />
              </div>
            )}

            <div>
              <label className="text-earth-600 text-xs font-semibold uppercase tracking-wider mb-1 block">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
                data-testid="auth-email-input" autoComplete="email" required
                className="bg-earth-100 border-0 rounded-2xl px-5 py-3.5 text-earth-800 w-full focus:ring-2 focus:ring-terracotta/30 focus:outline-none placeholder:text-earth-400 text-sm" />
            </div>

            <div className="relative">
              <label className="text-earth-600 text-xs font-semibold uppercase tracking-wider mb-1 block">Password</label>
              <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password"
                data-testid="auth-password-input" autoComplete={mode === "login" ? "current-password" : "new-password"} required
                className="bg-earth-100 border-0 rounded-2xl px-5 py-3.5 text-earth-800 w-full focus:ring-2 focus:ring-terracotta/30 focus:outline-none placeholder:text-earth-400 text-sm pr-12" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-8 text-earth-400 hover:text-earth-600">
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {error && (
              <div className="bg-terracotta-light rounded-2xl px-4 py-3" data-testid="auth-error">
                <p className="text-terracotta text-sm">{error}</p>
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="auth-submit-btn"
              className="w-full bg-terracotta text-earth-50 rounded-full py-3.5 font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
              {!loading && <ArrowRight size={18} />}
            </button>
          </form>

          <p className="text-earth-400 text-xs text-center mt-6">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-terracotta font-medium hover:underline">
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </div>

        <p className="text-earth-400 text-[11px] text-center mt-6 max-w-sm mx-auto leading-relaxed">
          Your data stays private and secure. Sovha is a peer-support companion, not a medical service.
        </p>
      </div>
    </div>
  );
}
